/**
 * Minimal fetch wrapper for the embed widget's chat-bot API surface.
 *
 * Every call site (history hydration, config fetch, client-log beacon,
 * launcher log) needs the same four things:
 *   - Prepend `API_ENDPOINT` to a `/chat-bot/*` path.
 *   - `Authorization: Bearer <apiKey>`.
 *   - `Content-Type: application/json` on request methods that carry a
 *     body — GET/HEAD get no default.
 *   - Mint (or accept) an `X-Request-Id` and return it so callers can
 *     thread it into correlated logs.
 *
 * Auto-logging: network failures and non-2xx responses are automatically
 * routed to a module-scope error logger. Each surface (SPA, launcher)
 * calls `setHttpClientErrorLogger` once at boot to bind its own log
 * function — the SPA binds to `logError`, the launcher binds to a thin
 * `launcherLog` adapter. Zero boilerplate at call sites; no static import
 * of `clientLog` from `httpClient` so the launcher bundle stays lean.
 *
 * Requests to the client-log endpoint itself are exempt from auto-logging
 * to prevent recursion (a failed log-transport POST would otherwise log
 * itself).
 *
 * Intentionally NOT a full axios-style client with interceptors, retries,
 * response mapping, redaction, etc. — that'd blow the SPA bundle budget
 * and is orders of magnitude too heavy for the ~6 KB launcher IIFE.
 */

import { generateSafeUUID } from '@circuit/circuit-utils/id';

import { apiUrl, CHAT_BOT_ROUTES } from './routes';

export interface HttpClientErrorDetails {
  /** Underlying JS error (network failures only — undefined for non-2xx). */
  error?: unknown;
  method: string;
  path: string;
  requestId: string;
  /** HTTP status when the failure was a non-2xx response. */
  status?: number;
  /** HTTP status text ("Unauthorized", "Not Found", etc.) when available. */
  statusText?: string;
}

export type HttpClientErrorLogger = (details: HttpClientErrorDetails) => void;

let errorLogger: HttpClientErrorLogger | null = null;

/**
 * Bind the module-scope error logger. Call once at surface boot:
 *   - SPA (`main.tsx`) binds to `logError`.
 *   - Launcher (`index.ts`) binds to `launcherLog`.
 * A no-op default keeps callers safe when no logger has been set (tests,
 * or pre-boot init races).
 */
export function setHttpClientErrorLogger(fn: HttpClientErrorLogger): void {
  errorLogger = fn;
}

export interface HttpClientOptions extends RequestInit {
  apiKey: string;
  /**
   * Injected fetch (tests, or the launcher's config-fetch which currently
   * accepts an injected fetcher for the same reason). Defaults to global.
   */
  fetcher?: typeof globalThis.fetch;
  /**
   * Host page origin, emitted as `X-Host-Origin` when set. The API's CORS
   * middleware uses it to validate iframe-phase calls against the customer's
   * domain allowlist — the browser-stamped `Origin` on those calls is always
   * the iframe's own host and can't do the job.
   */
  hostOrigin?: string;
  /** Route path from `CHAT_BOT_ROUTES`. */
  path: string;
  /**
   * Optional caller-supplied X-Request-Id. Pass the same id you attached
   * to an earlier BE request to keep the two correlated in Datadog.
   * Auto-generated (safe UUID) when omitted.
   */
  requestId?: string;
}

export interface HttpClientResult {
  requestId: string;
  response: Response;
}

function reportError(details: HttpClientErrorDetails): void {
  // Exempt the client-log endpoint itself — a failing log transport must
  // not try to log its own failure via the same transport.
  if (details.path === CHAT_BOT_ROUTES.clientLog) return;
  errorLogger?.(details);
}

export async function httpClient({
  apiKey,
  fetcher = globalThis.fetch.bind(globalThis),
  headers,
  hostOrigin,
  path,
  requestId,
  ...init
}: HttpClientOptions): Promise<HttpClientResult> {
  const id = requestId ?? generateSafeUUID();
  const method = init.method?.toUpperCase() ?? 'GET';
  const mergedHeaders: Record<string, string> = {
    Authorization: `Bearer ${apiKey}`,
    'X-Request-Id': id,
    ...(headers as Record<string, string> | undefined),
  };
  if (hostOrigin && !mergedHeaders['X-Host-Origin']) {
    mergedHeaders['X-Host-Origin'] = hostOrigin;
  }
  if (method !== 'GET' && method !== 'HEAD' && !mergedHeaders['Content-Type']) {
    mergedHeaders['Content-Type'] = 'application/json';
  }
  let response: Response;
  try {
    response = await fetcher(apiUrl(path), { ...init, headers: mergedHeaders });
  } catch (error) {
    // Don't log user-initiated aborts — they're normal cancellations, not failures.
    const isAbort =
      error instanceof DOMException && error.name === 'AbortError';
    if (!isAbort) {
      reportError({ error, method, path, requestId: id });
    }
    throw error;
  }
  if (!response.ok) {
    reportError({
      method,
      path,
      requestId: id,
      status: response.status,
      statusText: response.statusText,
    });
  }
  return { requestId: id, response };
}
